import { colors } from '@lmc/common/constants'
import { PageHeader } from '@lmc/ui-kit'
import _ from 'lodash'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import {
  HelpLink,
  NoDataFound,
  Spinner,
  Table
} from '../../../components/generic'
import Operations from '../../../operations'

/*
Chooses which temperature alert icon to display
*/
const testTemperature = (temperature) => {
  const alertIconStyle =
    temperature < TEMPERATURE_UPPER_BOUND
      ? styles.alertIcons.warning
      : styles.alertIcons.danger

  return alertIconStyle
}

/*
Chooses which respiration rate alert icon to display
*/
const testRespirationRate = (respirationRate) => {
  const alertIconStyle =
    respirationRate < RR_UPPER_BOUND
      ? styles.alertIcons.warning
      : styles.alertIcons.danger

  return alertIconStyle
}

/*
Formats the symptoms data to include alert icons.
*/
const formatSymptoms = (symptoms) => {
  if (!symptoms) return { symptoms: [], value: 0 }
  let alertIconStyle
  let COVIDSymptomsCount = 0
  symptoms[0] = _.capitalize(symptoms[0])

  const formattedSymptoms = symptoms.map((symptom) => {
    const isCOVIDSymptom = _.find(ALERT_SYMPTOMS, (pattern) =>
      symptom.match(pattern)
    )
    if (isCOVIDSymptom) {
      COVIDSymptomsCount += 1
    }

    return { symptom: symptom.replace(/,/, '') }
  })

  // If the COVID symptom count is 1, the alert icon is
  //  orange. More than 1, and it's red.
  if (COVIDSymptomsCount) {
    alertIconStyle =
      COVIDSymptomsCount === 1
        ? styles.alertIcons.warning
        : styles.alertIcons.danger
  }

  // Sets value of symptomsCount. Allows residents to
  // first be sorted into alert levels, then be sorted by
  // number of symptoms within a given alert level.
  let symptomsCount = 0
  if (COVIDSymptomsCount > 1) {
    symptomsCount = symptoms.length + 40
  } else if (COVIDSymptomsCount === 1) {
    symptomsCount = symptoms.length + 20
  } else {
    symptomsCount = symptoms.length
  }

  return { symptoms: formattedSymptoms, alertIconStyle, value: symptomsCount }
}

/*
Formats the covid test result data to include alert icons.
*/
const formatCovidTestResult = (covidTest) => {
  if (!covidTest) return {}
  const isPositive = covidTest.match(/Positive/i)
  const alertIconStyle = isPositive ? styles.alertIcons.danger : null

  return {
    covidTest,
    alertIconStyle,
    displayAlert: isPositive,
    value: covidTest
  }
}

const sortData = (data, sort, allResidents) => {
  if (sort.key === 'residents') {
    const sortedData = _.orderBy(data, (entry) => {
      const resident = _.find(allResidents, { id: entry.key })
      return _.get(resident, 'name.first')
    })

    return sortedData
  }

  return _.orderBy(data, (row) => {
    const sortOrder = sort.order === 'asc' ? 1 : -1
    const value = row.data[sort.key]
    return sortOrder * value
  })
}

const getTableData = (data, allResidents) => {
  const tableData = data
    .map((row) => {
      const resident = _.find(allResidents, { id: row.key })
      if (!resident) return null
      let residentName = `${resident.name.first} ${resident.name.last}`
      residentName = _.truncate(residentName, { length: 25 })

      const { temperature } = row.data
      const { respirationRate } = row.data
      const symptoms = formatSymptoms(row.data.symptoms)
      const covidTest = formatCovidTestResult(row.data.covidTest)

      const displayTempAlert = temperature >= TEMPERATURE_LOWER_BOUND
      const displayRRAlert = respirationRate >= RR_LOWER_BOUND

      const tempAlertStyle = displayTempAlert
        ? testTemperature(temperature)
        : null

      const rrAlertStyle = displayRRAlert
        ? testRespirationRate(respirationRate)
        : null

      return [
        { value: residentName, picture: resident.picture },
        {
          value: temperature,
          displayAlert: displayTempAlert,
          alertIconStyle: tempAlertStyle
        },
        {
          value: respirationRate,
          displayAlert: displayRRAlert,
          alertIconStyle: rrAlertStyle
        },
        symptoms,
        covidTest
      ]
    })
    .filter((d) => d)
  return tableData
}

const renderLoading = () => (
  <p>
    <Spinner />
  </p>
)

const renderEmpty = () => (
  <NoDataFound
    heading="Nothing to see here..."
    text="Your home has no data to display."
    linkText="Click here for help."
    url={SUPPORT_ARTICLE}
  />
)

const renderError = (t) => (
  <NoDataFound
    heading={t('reports.coronavirus.errorTitle')}
    text={t('reports.coronavirus.errorDescription')}
    linkText="Click here for help."
    url={SUPPORT_ARTICLE}
  />
)

const renderContent = (
  data,
  sort,
  setSort,
  allResidents,
  preferredPeopleNominative
) => {
  const columns = [
    {
      label: `${preferredPeopleNominative}`,
      key: 'residents',
      sortable: true,
      headerStyle: { padding: '7px 0 7px 0' },
      type: 'profile',
      width: 250
    },
    {
      label: 'Temp (\xB0C)',
      key: 'temperature',
      headerStyle: { fontWeight: 'normal', padding: '7px 0 7px 0' },
      type: 'alert',
      sortable: true,
      width: 120
    },
    {
      label: 'Respiration Rate (breaths/min)',
      key: 'respirationRate',
      headerStyle: { fontWeight: 'normal', padding: '7px 0 7px 0' },
      type: 'alert',
      sortable: true
    },
    {
      label: 'Symptoms logged',
      key: 'symptoms',
      headerStyle: { fontWeight: 'normal', padding: '7px 0 7px 0' },
      symptomsIcon: { color: colors.darkgrey },
      type: 'symptoms',
      sortable: true
    },
    {
      label: 'Testing',
      key: 'covidTest',
      headerStyle: { fontWeight: 'normal', padding: '7px 0 7px 0' },
      type: 'alert',
      sortable: true,
      width: 150
    }
  ]

  const sortedData = sortData(data, sort, allResidents)
  const tableData = getTableData(sortedData, allResidents)
  if (!tableData || !tableData.length) return renderEmpty()

  return (
    <div>
      <div style={styles.subheader}>
        <div style={styles.subheaderText}>
          <span>Over the last 2 days</span>
        </div>
      </div>
      <Table
        className="lmc-table-gridcell-height"
        data={tableData}
        columns={columns}
        defaultSorted={{ title: `${preferredPeopleNominative}`, sorted: 'asc' }}
        onSortChange={(newSort) => setSort(newSort)}
      />
      <HelpLink supportLink={SUPPORT_ARTICLE} style={styles.helpLink} />
    </div>
  )
}

export function CoronavirusScreen(props) {
  const {
    allResidents,
    data,
    error,
    fetchData,
    isLoading,
    preferredPeopleNominative
  } = props

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    fetchData()
  }, [])

  const [sort, setSort] = useState({ key: 'residents', order: 'asc' })
  const { t } = useTranslation()

  // A failed request has to be distinguishable from a home with genuinely no
  // observations, so `error` is checked before falling back to the empty state.
  // It is also checked before `allResidents`, which is a separate request: the
  // reducer clears `error` on every new fetch, so it can only be set once this
  // request has settled, and waiting on the residents list would hide it.
  const renderBody = () => {
    if (error) return renderError(t)
    if (isLoading || !allResidents) return renderLoading()
    if (!data) return renderEmpty()

    return renderContent(
      data,
      sort,
      setSort,
      allResidents,
      preferredPeopleNominative
    )
  }

  return (
    <div style={styles.container}>
      <PageHeader title="Coronavirus Monitoring Tool" />
      <div style={{ marginTop: 5 }} />
      {renderBody()}
    </div>
  )
}

const styles = {
  alertIcons: {
    warning: {
      name: 'overdue',
      color: colors.orange
    },
    danger: {
      name: 'overdue',
      color: colors.red
    }
  },
  container: {
    marginRight: 'auto',
    marginLeft: 0,
    padding: '24px 24px',
    position: 'relative'
  },
  headerSection: {
    marginBottom: 10
  },
  helpLink: {
    float: 'left',
    marginTop: 20,
    paddingBottom: 20
  },
  subheader: {
    fontWeight: 'bold',
    padding: '20px 0 0px 250px'
  },
  subheaderText: {
    borderBottom: `1px solid ${colors.grey}`,
    paddingBottom: '8px'
  },
  subtext: {
    padding: '5px 0 0 0'
  }
}

CoronavirusScreen.propTypes = {
  allResidents: PropTypes.array,
  data: PropTypes.array,
  error: PropTypes.string,
  isLoading: PropTypes.bool
}

const SUPPORT_ARTICLE =
  'https://support.logmycare.co.uk/en/articles/3849272-how-do-i-monitor-coronavirus-with-the-care-office'
const STATE_ID = 'reports_covid'
const TEMPERATURE_LOWER_BOUND = 37.2
const TEMPERATURE_UPPER_BOUND = 37.8
const RR_LOWER_BOUND = 20
const RR_UPPER_BOUND = 30
const ALERT_SYMPTOMS = [/fever/i, /cough/i, /smell/i, /taste/i]

const mapStateToProps = (state) => ({
  allResidents: state.data.residents,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  isLoading: _.get(state, `data.requests.${STATE_ID}.isLoading`),
  error: _.get(state, `data.requests.${STATE_ID}.error`),
  data: _.get(state, `data.requests.${STATE_ID}.response`)
})

const mapDispatchToProps = (dispatch) => ({
  fetchData: () => {
    dispatch(
      Operations.fetchData({
        url: '/aggregations/covid',
        id: STATE_ID
      })
    )
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(CoronavirusScreen)
